import { getOrCreateVisitorId } from "../utils/portalVisitor";
import {
  isAffiliateAttributionEnabled,
  isAffiliateAttributionV2Enabled,
} from "../utils/affiliateRollout";

const rawApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
if (!rawApiBaseUrl || !rawApiBaseUrl.trim()) {
  throw new Error("Missing REACT_APP_API_BASE_URL. Set it in the deploy env file used for build.");
}
const API_BASE_URL = rawApiBaseUrl.trim();
const AUTH_REFRESH_PATH = "/portal/auth/refresh";
const PORTAL_AUTH_PREFIX = "/portal/auth/";
const SESSION_FLAG_KEY = "portal_has_session";
const AUTH_EXPIRED_EVENT = "portal-auth:expired";
const AUTH_EXPIRED_MESSAGE = "Session expired. Kindly login again.";
const PUBLIC_REQUEST_RETRY_BASE_DELAY_MS = 100;
const PUBLIC_REQUEST_RETRY_JITTER_MS = 150;
const RETRYABLE_PUBLIC_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const PUBLIC_ANONYMOUS_GET_PREFIXES = [
  "/destinations",
  "/districts",
  "/explore",
  "/homepage",
  "/packages",
  "/places",
  "/website",
  "/v1/landing",
];
export const PUBLIC_REQUEST_CANCELLATION_OUTCOMES = Object.freeze({
  component: "cancelled_by_component",
  navigation: "cancelled_by_navigation",
});
export const PUBLIC_REQUEST_BUDGETS = Object.freeze({
  landing: Object.freeze({ timeoutMs: 12000, firstAttemptTimeoutMs: 8000, retries: 1 }),
  detail: Object.freeze({ timeoutMs: 15000, firstAttemptTimeoutMs: 10000, retries: 1 }),
  feed: Object.freeze({ timeoutMs: 12000, firstAttemptTimeoutMs: 8000, retries: 1 }),
  configuration: Object.freeze({ timeoutMs: 8000, firstAttemptTimeoutMs: 5000, retries: 1 }),
  default: Object.freeze({ timeoutMs: 7500, firstAttemptTimeoutMs: 4500, retries: 1 }),
});
let geoProvider;
let sessionCleaner;
let refreshPromise;
let authExpiryNotifiedAt = 0;
const inFlightAnonymousRequests = new Map();

function normalizeMethod(value = "GET") {
  return String(value || "GET").trim().toUpperCase() || "GET";
}

function normalizeRequestPath(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "/";
  const pathname = raw.split(/[?#]/, 1)[0] || "/";
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return normalized.length > 1 ? normalized.replace(/\/+$/, "") : normalized;
}

function hasHeader(headers = {}, name = "") {
  const normalizedName = String(name || "").trim().toLowerCase();
  return Object.keys(headers || {}).some(
    (headerName) => String(headerName).trim().toLowerCase() === normalizedName,
  );
}

function isPublicAnonymousGet(path, method = "GET") {
  const normalizedMethod = normalizeMethod(method);
  if (normalizedMethod !== "GET" && normalizedMethod !== "HEAD") return false;
  const normalizedPath = normalizeRequestPath(path);
  return PUBLIC_ANONYMOUS_GET_PREFIXES.some(
    (prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`),
  );
}

function readBrowserRoute() {
  if (typeof window === "undefined") return "";
  return String(window.location?.pathname || "");
}

const normalizeCancellationOutcome = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (
    normalized === PUBLIC_REQUEST_CANCELLATION_OUTCOMES.navigation
    || ["navigation", "route-change", "route_change"].includes(normalized)
  ) return PUBLIC_REQUEST_CANCELLATION_OUTCOMES.navigation;
  if (
    normalized === PUBLIC_REQUEST_CANCELLATION_OUTCOMES.component
    || ["component", "component-unmount", "component_unmount"].includes(normalized)
  ) return PUBLIC_REQUEST_CANCELLATION_OUTCOMES.component;
  return "";
};

export function resolvePublicRequestCancellationOutcome(
  signal,
  { startedRoute = "", fallback = PUBLIC_REQUEST_CANCELLATION_OUTCOMES.component } = {},
) {
  const reason = signal?.reason;
  const explicit = normalizeCancellationOutcome(
    reason?.outcome || reason?.cancellationType || (typeof reason === "string" ? reason : ""),
  );
  if (explicit) return explicit;
  const currentRoute = readBrowserRoute();
  if (startedRoute && currentRoute && startedRoute !== currentRoute) {
    return PUBLIC_REQUEST_CANCELLATION_OUTCOMES.navigation;
  }
  return normalizeCancellationOutcome(fallback) || PUBLIC_REQUEST_CANCELLATION_OUTCOMES.component;
}

export function createPublicRequestCancellation(cancellationType = "component") {
  const outcome = normalizeCancellationOutcome(cancellationType)
    || PUBLIC_REQUEST_CANCELLATION_OUTCOMES.component;
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";
  error.status = 0;
  error.outcome = outcome;
  error.cancellationType = outcome === PUBLIC_REQUEST_CANCELLATION_OUTCOMES.navigation
    ? "navigation"
    : "component";
  return error;
}

function createTimeoutError() {
  const error = new Error("The public request timed out. Please try again.");
  error.name = "TimeoutError";
  error.status = 0;
  error.outcome = "timeout";
  return error;
}

const isPublicDetailPath = (path = "") => (
  /^\/destinations\/[^/]+$/.test(path)
  || /^\/districts\/[^/]+(?:\/guide)?$/.test(path)
  || /^\/packages\/families\/[^/]+$/.test(path)
  || /^\/packages\/[^/]+$/.test(path)
  || /^\/explore\/destinations\/[^/]+\/camps\/[^/]+$/.test(path)
  || /^\/explore\/(?:experiences|cruises)\/[^/]+\/[^/]+$/.test(path)
  || /^\/explore\/experience-categories\/[^/]+$/.test(path)
  || /^\/explore\/experiences\/category\/[^/]+(?:\/[^/]+)?$/.test(path)
  || /^\/explore\/treks\/(?:[^/]+\/)?[^/]+$/.test(path)
);

export function resolvePublicRequestBudget(path, options = {}) {
  const normalizedPath = normalizeRequestPath(path);
  let profile = PUBLIC_REQUEST_BUDGETS.default;
  if (normalizedPath.startsWith("/v1/landing")) {
    profile = PUBLIC_REQUEST_BUDGETS.landing;
  } else if (normalizedPath.includes("/feed") || [
    "/destinations",
    "/districts",
    "/explore/camps",
    "/explore/stats",
    "/explore/experience-categories",
    "/packages",
    "/packages/families",
  ].includes(normalizedPath) || normalizedPath.startsWith("/homepage/")) {
    profile = PUBLIC_REQUEST_BUDGETS.feed;
  } else if (
    /^\/explore\/destinations\/[^/]+\/(?:camps|experiences)$/.test(normalizedPath)
    || /^\/explore\/destinations\/[^/]+\/camps\/[^/]+\/rooms$/.test(normalizedPath)
    || /^\/destinations\/[^/]+\/composition$/.test(normalizedPath)
  ) {
    profile = PUBLIC_REQUEST_BUDGETS.feed;
  } else if (isPublicDetailPath(normalizedPath)) {
    profile = PUBLIC_REQUEST_BUDGETS.detail;
  } else if (normalizedPath.startsWith("/website/")) {
    profile = PUBLIC_REQUEST_BUDGETS.configuration;
  }

  const requestedTimeout = Number(options.timeoutMs);
  const timeoutMs = Math.max(1, Number.isFinite(requestedTimeout) && requestedTimeout > 0
    ? requestedTimeout
    : profile.timeoutMs);
  const requestedFirstAttempt = Number(options.firstAttemptTimeoutMs);
  const firstAttemptTimeoutMs = Math.min(timeoutMs, Math.max(1,
    Number.isFinite(requestedFirstAttempt) && requestedFirstAttempt > 0
      ? requestedFirstAttempt
      : profile.firstAttemptTimeoutMs,
  ));
  const requestedRetries = Number(options.retries);
  const hasRetryOverride = options.retries !== undefined
    && options.retries !== null
    && options.retries !== ""
    && Number.isFinite(requestedRetries);
  const retries = Math.max(0, hasRetryOverride
    ? Math.floor(requestedRetries)
    : profile.retries);
  return { timeoutMs, firstAttemptTimeoutMs, retries };
}

function createAttemptSignal(inputSignal, timeoutMs) {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort(inputSignal?.reason);
  if (inputSignal?.aborted) controller.abort(inputSignal.reason);
  else inputSignal?.addEventListener?.("abort", abortFromCaller, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, Math.max(1, timeoutMs));
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    cleanup: () => {
      clearTimeout(timer);
      inputSignal?.removeEventListener?.("abort", abortFromCaller);
    },
  };
}

function waitForPublicRetry(signal, delayMs, startedRoute = "") {
  if (signal?.aborted) {
    return Promise.reject(createPublicRequestCancellation(
      resolvePublicRequestCancellationOutcome(signal, { startedRoute }),
    ));
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener?.("abort", handleAbort);
      resolve();
    }, delayMs);
    const handleAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener?.("abort", handleAbort);
      reject(createPublicRequestCancellation(
        resolvePublicRequestCancellationOutcome(signal, { startedRoute }),
      ));
    };
    signal?.addEventListener?.("abort", handleAbort, { once: true });
  });
}

async function fetchPublicWithRetry(url, config, options = {}) {
  const budget = resolvePublicRequestBudget(options.path || url, options);
  const totalTimeoutMs = budget.timeoutMs;
  const retries = budget.retries;
  const startedRoute = options.startedRoute || "";
  const deadline = Date.now() + totalTimeoutMs;
  let attempt = 0;

  while (attempt <= retries) {
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) throw createTimeoutError();
    const attemptTimeoutMs = attempt === 0 && retries > 0
      ? Math.min(budget.firstAttemptTimeoutMs, remainingMs)
      : remainingMs;
    const attemptSignal = createAttemptSignal(config.signal, attemptTimeoutMs);
    let response;
    let requestError;
    try {
      response = await fetch(url, { ...config, signal: attemptSignal.signal });
    } catch (error) {
      requestError = attemptSignal.timedOut() ? createTimeoutError() : error;
    } finally {
      attemptSignal.cleanup();
    }

    if (config.signal?.aborted) {
      throw createPublicRequestCancellation(
        resolvePublicRequestCancellationOutcome(config.signal, { startedRoute }),
      );
    }
    const retryableResponse = response && RETRYABLE_PUBLIC_STATUSES.has(Number(response.status));
    const retryableError = Boolean(requestError);
    if (attempt < retries && (retryableResponse || retryableError)) {
      const retryDelayMs = PUBLIC_REQUEST_RETRY_BASE_DELAY_MS
        + Math.floor(Math.random() * PUBLIC_REQUEST_RETRY_JITTER_MS);
      const remainingBeforeRetry = deadline - Date.now();
      if (remainingBeforeRetry <= retryDelayMs) throw requestError || createTimeoutError();
      await waitForPublicRetry(config.signal, retryDelayMs, startedRoute);
      attempt += 1;
      continue;
    }
    if (requestError) throw requestError;
    return response;
  }

  throw createTimeoutError();
}

function shouldDedupeAnonymousRequest(path, options = {}) {
  if (options.dedupe === false || options.retrying || options.keepalive) return false;
  const method = normalizeMethod(options.method);
  const headers = { ...(options.headers || {}) };
  if (Object.keys(headers).length > 0) return false;
  const anonymous =
    options.anonymous ??
    (isPublicAnonymousGet(path, method) && !hasHeader(headers, "Authorization"));
  if (!anonymous || (method !== "GET" && method !== "HEAD")) return false;
  if (options.credentials && options.credentials !== "omit") return false;
  if (options.includeGeoHeaders === true || options.includeVisitorId === true) return false;
  return true;
}

function releaseAnonymousRequest(entry) {
  entry.subscribers = Math.max(0, entry.subscribers - 1);
  if (entry.subscribers > 0 || entry.settled || entry.abortTimer) return;
  // React StrictMode immediately remounts effects in development. A zero-delay
  // grace period lets the replacement subscriber reuse the same network call.
  entry.abortTimer = setTimeout(() => {
    entry.abortTimer = null;
    if (!entry.settled && entry.subscribers === 0) {
      entry.controller.abort();
    }
  }, 0);
}

function subscribeToAnonymousRequest(entry, signal) {
  const startedRoute = readBrowserRoute();
  if (signal?.aborted) {
    return Promise.reject(createPublicRequestCancellation(
      resolvePublicRequestCancellationOutcome(signal, { startedRoute }),
    ));
  }
  if (entry.abortTimer) {
    clearTimeout(entry.abortTimer);
    entry.abortTimer = null;
  }
  entry.subscribers += 1;

  return new Promise((resolve, reject) => {
    let active = true;
    const finish = (callback, value) => {
      if (!active) return;
      active = false;
      signal?.removeEventListener?.("abort", handleAbort);
      releaseAnonymousRequest(entry);
      callback(value);
    };
    const handleAbort = () => finish(reject, createPublicRequestCancellation(
      resolvePublicRequestCancellationOutcome(signal, { startedRoute }),
    ));
    signal?.addEventListener?.("abort", handleAbort, { once: true });
    entry.promise.then(
      (value) => finish(resolve, value),
      (error) => finish(reject, error),
    );
  });
}

function requestAnonymousOnce(path, options = {}) {
  if (options.signal?.aborted) {
    return Promise.reject(createPublicRequestCancellation(
      resolvePublicRequestCancellationOutcome(options.signal, { startedRoute: readBrowserRoute() }),
    ));
  }
  const method = normalizeMethod(options.method);
  const key = `${method}:${path}`;
  let entry = inFlightAnonymousRequests.get(key);
  if (!entry) {
    const controller = new AbortController();
    entry = {
      controller,
      subscribers: 0,
      abortTimer: null,
      settled: false,
      promise: null,
    };
    entry.promise = executeRequest(path, {
      ...options,
      signal: controller.signal,
      dedupe: false,
    });
    inFlightAnonymousRequests.set(key, entry);
    const settle = () => {
      entry.settled = true;
      if (entry.abortTimer) clearTimeout(entry.abortTimer);
      if (inFlightAnonymousRequests.get(key) === entry) {
        inFlightAnonymousRequests.delete(key);
      }
    };
    entry.promise.then(settle, settle);
  }
  return subscribeToAnonymousRequest(entry, options.signal);
}

async function ensureSessionCleaner() {
  if (!sessionCleaner) {
    try {
      sessionCleaner = await import("../utils/authTokens");
    } catch {
      sessionCleaner = null;
    }
  }
  return sessionCleaner;
}

function notifyAuthExpired(message = AUTH_EXPIRED_MESSAGE) {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - authExpiryNotifiedAt < 2000) return;
  authExpiryNotifiedAt = now;
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { message } }));
  } catch {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  }
}

function emitApiRequestTelemetry({
  path,
  method,
  status = 0,
  duration = 0,
  outcome,
  cacheStatus = "",
  payloadBytes = 0,
  negative = false,
}) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  window.dispatchEvent(new CustomEvent("ecotour:telemetry", {
    detail: {
      event: "api_request",
      endpoint: path,
      method,
      status,
      duration,
      outcome,
      cacheStatus,
      payloadBytes,
      negative,
    },
  }));
}

export function classifyPublicRequestError(error, { signal, startedRoute = "" } = {}) {
  if (error?.name === "TimeoutError" || error?.outcome === "timeout") return "timeout";
  if (error?.name === "AbortError" || signal?.aborted) {
    return normalizeCancellationOutcome(error?.outcome)
      || resolvePublicRequestCancellationOutcome(signal, { startedRoute });
  }
  return "network_error";
}

async function clearAuthState({ notify = false } = {}) {
  try {
    const provider = await ensureSessionCleaner();
    const { clearTokens } = provider || {};
    const sessionStore = await import("../utils/nightStayAccount").catch(() => null);
    if (clearTokens) clearTokens();
    sessionStore?.clearSession?.();
  } catch (_) {
    // ignore cleanup errors
  }
  if (notify) notifyAuthExpired();
}

function hasPortalSession() {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(
      window.sessionStorage?.getItem(SESSION_FLAG_KEY) ||
        window.localStorage?.getItem(SESSION_FLAG_KEY)
    );
  } catch {
    return false;
  }
}

function isPortalAuthRoute(path = "") {
  return String(path || "").startsWith(PORTAL_AUTH_PREFIX);
}

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${AUTH_REFRESH_PATH}`, {
        method: "POST",
        credentials: "include",
      });
      return response.ok;
    } catch {
      return false;
    }
  })();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function withGeoHeaders(headers = {}) {
  if (!geoProvider) {
    try {
      geoProvider = await import("../utils/clientGeo");
    } catch {
      geoProvider = null;
    }
  }
  if (geoProvider?.getGeoHeaders) {
    try {
      const geoHeaders = await geoProvider.getGeoHeaders();
      return { ...headers, ...geoHeaders };
    } catch {
      // ignore geo errors
    }
  }
  return headers;
}

async function executeRequest(path, options = {}) {
  const startedRoute = readBrowserRoute();
  const { retrying } = options;
  const method = normalizeMethod(options.method);
  const suppliedHeaders = { ...(options.headers || {}) };
  const anonymous =
    options.anonymous ??
    (isPublicAnonymousGet(path, method) && !hasHeader(suppliedHeaders, "Authorization"));
  const includeGeoHeaders = options.includeGeoHeaders ?? !anonymous;
  const includeVisitorId = options.includeVisitorId ?? !anonymous;
  const supportsBody = method !== "GET" && method !== "HEAD";
  let body = supportsBody ? options.body : undefined;

  if (body !== undefined && typeof body !== "string") {
    body = JSON.stringify(body);
  }
  if (body !== undefined && !hasHeader(suppliedHeaders, "Content-Type")) {
    suppliedHeaders["Content-Type"] = "application/json";
  }

  let config = {
    method,
    headers: suppliedHeaders,
    body,
    signal: options.signal,
    credentials: options.credentials || (anonymous ? "omit" : "include"),
    keepalive: Boolean(options.keepalive),
    cache: options.browserCache,
  };

  if (!supportsBody) {
    delete config.body;
  }
  if (!config.cache) {
    delete config.cache;
  }

  if (includeGeoHeaders) {
    config.headers = await withGeoHeaders(config.headers);
  }
  if (includeVisitorId && isAffiliateAttributionEnabled()) {
    const visitorId = getOrCreateVisitorId() || "";
    if (visitorId) {
      config.headers["x-visitor-id"] = visitorId;
    }
  }
  if (includeVisitorId && isAffiliateAttributionV2Enabled()) {
    config.headers["x-affiliate-attribution-v2"] = "phase4";
  }

  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  let response;
  try {
    response = anonymous && (method === "GET" || method === "HEAD")
      ? await fetchPublicWithRetry(`${API_BASE_URL}${path}`, config, {
          path,
          timeoutMs: options.timeoutMs,
          firstAttemptTimeoutMs: options.firstAttemptTimeoutMs,
          retries: options.retries,
          startedRoute,
        })
      : await fetch(`${API_BASE_URL}${path}`, config);
  } catch (error) {
    const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    const outcome = classifyPublicRequestError(error, { signal: config.signal, startedRoute });
    emitApiRequestTelemetry({
      path,
      method,
      status: 0,
      duration: Math.max(0, endedAt - startedAt),
      outcome,
    });
    if (outcome === "timeout" && error?.outcome !== "timeout") throw createTimeoutError();
    if (outcome.startsWith("cancelled_by_") && error?.outcome !== outcome) {
      throw createPublicRequestCancellation(outcome);
    }
    throw error;
  }
  const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  emitApiRequestTelemetry({
    path,
    method,
    status: response.status,
    duration: Math.max(0, endedAt - startedAt),
    outcome: response.ok ? "ok" : "http_error",
    cacheStatus: String(response.headers?.get?.("x-cache") || "").trim().toLowerCase(),
    payloadBytes: Number(
      response.headers?.get?.("x-payload-bytes")
      || response.headers?.get?.("content-length")
      || 0,
    ) || 0,
    negative: response.headers?.get?.("x-cache-negative") === "1",
  });
  if (!response.ok) {
    let sessionExpired = false;
    if (response.status === 401) {
      const hadSession = hasPortalSession();
      if (hadSession && !retrying && path !== AUTH_REFRESH_PATH) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          return request(path, { ...options, retrying: true });
        }
      }
      if (hadSession && !isPortalAuthRoute(path)) {
        sessionExpired = true;
        await clearAuthState({ notify: true });
      }
    }
    const error = new Error("Request failed");
    error.status = response.status;
    try {
      const data = await response.json();
      error.message = data?.message || error.message;
      error.data = data;
      if (Array.isArray(data?.fieldErrors)) {
        error.fieldErrors = data.fieldErrors;
      }
    } catch (_) {
      // ignore parse errors
    }
    if (sessionExpired) {
      error.message = AUTH_EXPIRED_MESSAGE;
    }
    throw error;
  }

  if (options.responseType === 'stream') return response;
  if (options.responseType === 'blob') return response.blob();
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function request(path, options = {}) {
  if (shouldDedupeAnonymousRequest(path, options)) {
    return requestAnonymousOnce(path, options);
  }
  return executeRequest(path, options);
}

export function __resetAnonymousRequestDedupeForTests() {
  inFlightAnonymousRequests.forEach((entry) => {
    if (entry.abortTimer) clearTimeout(entry.abortTimer);
    entry.controller.abort();
  });
  inFlightAnonymousRequests.clear();
}

const client = {
  get: (path, options = {}) => request(path, { method: "GET", ...options }),
  post: (path, body, options = {}) => request(path, { method: "POST", body, ...options }),
  put: (path, body, options = {}) => request(path, { method: "PUT", body, ...options }),
  patch: (path, body, options = {}) => request(path, { method: "PATCH", body, ...options }),
  delete: (path, options = {}) => request(path, { method: "DELETE", ...options }),
};

export default client;
